import MoreLinks from "@/components/home/MoreLink";
import VideoBanner from "@/components/home/VideoBanner";
import { useInView } from "react-intersection-observer";
import RequestItemForm from "@/components/requestItem/RequestItemForm";
import dynamic from "next/dynamic";
import Link from "next/link";
import Image from "next/image";
import StatsCounter from "@/components/widgets/StatsCounter";
import Testimonials from "@/components/widgets/Testimonials";
import PartnerLogosSlider from "@/components/widgets/PartnerLogosSlider";
import ProvinceWithCities from "@/components/widgets/ProvinceWithCities";
import CategoryWithProduct from "@/components/home/CategoryWithProduct";
import { SubscriptionPlan } from "@/components/pricing/SubscriptionPlan";
import Head from "next/head";
import { ENV } from "@/core/config/env";
import DownloadApp from "@/components/shared/DownloadApp";
import ProductServiceCarousel from "@/components/product/ProductServiceCarousel";
import { usePopup } from '@/contexts/PopupContext';
import { useCallback, useEffect, useState } from "react";

// Lazy-load popup but make it available immediately for user interactions
const RequestItemFormPopup = dynamic(() => import("@/components/requestItem/RequestItemFormPopup"), {
  ssr: false,
});

export default function HomePage() {
  // First intersection observer for lazy loading content
  const { ref: lazyLoadRef, inView: lazyLoadInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Second intersection observer for popup trigger at "New & Used Car Parts" heading
  const { ref: popupTriggerRef, inView: popupTriggerInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Track if user has manually triggered popup (clicked button)
  const [userTriggeredPopup, setUserTriggeredPopup] = useState(false);

  // Get service data from sessionStorage when carousel popup should open
  const { isPopupOpen, popupType, resetPopup } = usePopup();
  
  const getInitialData = useCallback(() => {
    if (typeof window !== 'undefined' && popupType === 'carousel') {
      const storedData = sessionStorage.getItem('selectedServiceData');
      if (storedData) {
        try {
          return JSON.parse(storedData);
        } catch (e) {
          console.error('Error parsing stored service data:', e);
        }
      }
    }
    return undefined;
  }, [popupType]);

  // Monitor popup state changes to detect user interactions
  useEffect(() => {
    if (isPopupOpen) {
      // Mark as user-triggered when popup opens
      setUserTriggeredPopup(true);
    }
  }, [isPopupOpen]);

  // Reset user trigger when popup closes AND popupTriggerInView is false
  useEffect(() => {
    if (!isPopupOpen && !popupTriggerInView) {
      // Only reset if we haven't scrolled to the trigger point yet
      setUserTriggeredPopup(false);
    }
  }, [isPopupOpen, popupTriggerInView]);

  // Once popupTriggerInView is true, keep popup available permanently
  useEffect(() => {
    if (popupTriggerInView) {
      setUserTriggeredPopup(false); // Reset since popupTriggerInView will handle it now
    }
  }, [popupTriggerInView]);

  const title = 'Stop Searching For Car Parts. Start Finding The Right Deal | CarSparesEzyFind'
  const description = 'Snap or scan it. Request it. Compare it. Get back on the road. Upload a photo of the part you need, scan or share your vehicle license details and location, and receive quotes from trusted spare shops, scrap yards and automotive businesses near you.';
  const keywords = 'scan license disc, car parts near me, car parts finder, scrap yards near me, motor spares, spare shops near me, car spares South Africa, used car parts, OEM car parts, aftermarket car parts, car part quotes, request for quote car parts, automotive parts marketplace, car parts app, find car parts online, compare car parts prices'
  const canonicalUrl = `${ENV.DOMAIN_URL}`;

  const siteUrl = ENV.DOMAIN_URL;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": `${siteUrl}/`,
        "name": "CarSparesEzyFind",
        "description": description,
        "potentialAction": {
          "@type": "SearchAction",
          "target": { "@type": "EntryPoint", "urlTemplate": `${siteUrl}/car-parts?q={search_term_string}` },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#business`,
        "name": "CarSparesEzyFind",
        "url": `${siteUrl}/`,
        "description": "South Africa's car parts marketplace. Find spare shops, scrap yards and automotive service providers near you. Compare quotes and get back on the road faster.",
        "areaServed": { "@type": "Country", "name": "South Africa" },
        "serviceType": ["Car Parts Marketplace", "Automotive Parts Finder", "Scrap Yard Directory", "Spare Shop Directory"]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do I find car parts near me on CarSparesEzyFind?",
            "acceptedAnswer": { "@type": "Answer", "text": "Upload a photo of the part you need, enter your vehicle details and location, then receive quotes from spare shops and scrap yards near you within a 20km radius." }
          },
          {
            "@type": "Question",
            "name": "Can I find used or second-hand car parts on CarSparesEzyFind?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes. CarSparesEzyFind connects you with scrap yards and suppliers offering new, used, OEM and aftermarket car parts so you can compare options and choose what works for your vehicle and budget." }
          },
          {
            "@type": "Question",
            "name": "Do I need to know the part name to request a quote?",
            "acceptedAnswer": { "@type": "Answer", "text": "No. Simply take a photo of the part you need, describe the problem, and include your vehicle details. Suppliers will respond with what they have available." }
          },
          {
            "@type": "Question",
            "name": "How does the reverse bidding for car parts work?",
            "acceptedAnswer": { "@type": "Answer", "text": "Submit your part request and multiple suppliers compete for your enquiry. You receive quotes from different spare shops and scrap yards, compare prices and availability, then choose the best deal." }
          }
        ]
      }
    ]
  };

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://www.CarSparesEzyFind.co.za/" />
        <meta property="og:site_name" content="CarSparesEzyFind" />
        <meta property="og:image" content="https://www.CarSparesEzyFind.co.za/images/Car-Spares-For-Sale-1200x511.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="511" />
        <meta property="article:publisher" content="https://www.facebook.com/CarSparesEzyfind.co.za" />
        <meta property="article:modified_time" content="2025-11-06T18:37:36+00:00" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://www.CarSparesEzyFind.co.za/images/Car-Spares-For-Sale-1200x511.webp" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      
      <div className="flex flex-col md:flex-row md:flex-wrap xl:flex-row">
        <div className="basis-12/12 md:basis-12/12 lg:order-1 xl:basis-9/16 xl:order-2 relative overflow-hidden mt-0">
          <VideoBanner />
        </div>
        <div id="rfq-form" className="basis-12/12 md:basis-6/12 lg:order-2 xl:basis-3/16 xl:order-1 relative top-0">
          <RequestItemForm 
            formClassName="h-[415px] xl:h-full border border-gray-300" 
            formId="homepage-embedded"
          />
        </div>
        <div className="basis-12/12 md:basis-6/12 lg:order-3 xl:basis-4/16 xl:order-3 mt-2 md:mt-0 md:px-1 md:pt-1 max-md:mb-5 overflow-hidden max-md:mx-[15px]">
          <MoreLinks />
        </div>
      </div>   
      
      {/* Hero value proposition — primary H1, lightweight, no images */}
      <section className="bg-white border-b border-gray-100 py-8 md:py-10">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-3">
            Stop Searching For Car Parts.{" "}
            <span className="text-[var(--primary-color)]">Start Finding The Right Deal.</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            Snap it. Request it. Compare it. Get back on the road — without calling ten shops or driving from scrapyard to scrapyard.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              href="/car-parts-spares-near-me"
             className="inline-block border-2 border-[var(--primary-color)] text-[var(--primary-color)] font-semibold px-6 py-3 rounded-md hover:bg-red-50 transition-colors text-sm sm:text-base"
            >
              Find My Part
            </Link>
            <Link
              href="#rfq-form"              
               className="inline-block bg-[var(--primary-color)] text-white font-semibold px-6 py-3 rounded-md hover:opacity-90 transition-opacity text-sm sm:text-base"
            >
              Request Multiple Quotes
            </Link>
          </div>
        </div>
      </section>

      {/* Full-width ProductServiceCarousel - spans entire browser width */}
      <div className="w-full">
        <ProductServiceCarousel className="w-full px-0" />
      </div>

      {/* Step sections */}
      <div className="bg-gray-50 py-15">
        <div className="container">
          <div className="md:w-8/11 xl:w-4/5 m-auto">
            <div className="flex flex-row max-lg:flex-col justify-center">
              <div className="basis-8/12 max-lg:basis-12/12 max-lg:order-2 mt-10 xl:pt-20 xl:pr-10">
                <h2 className="text-primary text-2xl sm:text-3xl md:text-4xl/10 font-bold uppercase mb-4 border-s-[5px] border-[var(--primary-color)] pl-5">Step 1: Scan Your License Disc — Get the Right Car Part, Every Time</h2>
                <p className="mb-3 text-md lg:text-xl">You know your car. We help you find what it needs. Scan your vehicle&apos;s license disc using our mobile app or upload a photo right here — so every quote is matched to your exact make, model and year.</p>
                
                <p className="mt-4 text-primary max-[640px]:text-lg max-[640px]:text-center">Car Parts App FREE Download! iPhone, Android & Huawei</p>
                <div className="flex flex-col items-center w-8/12 sm:w-full m-auto sm:flex-row md:gap-5 mt-3">
                  <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank">
                    <Image src="/images/app-store-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download iPhone" priority quality={85} placeholder="blur" blurDataURL="/images/app-store-btn.webp" sizes="(max-width: 640px) 80vw, (max-width: 1024px) 40vw, 20vw" />
                  </Link>
                  <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank">
                    <Image src="/images/google-play-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Android" priority quality={85} placeholder="blur" blurDataURL="/images/google-play-btn.webp" sizes="(max-width: 640px) 80vw, (max-width: 1024px) 40vw, 20vw" />
                  </Link>
                  <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank">
                    <Image src="/images/app-gallery-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Huawei" priority quality={85} placeholder="blur" blurDataURL="/images/app-gallery-btn.webp" sizes="(max-width: 640px) 80vw, (max-width: 1024px) 40vw, 20vw" />
                  </Link>
                </div>
              </div>
              <div className="basis-4/12 text-right max-lg:basis-12/12 max-lg:order-1 max-lg:text-center">
                <div className="relative p-1 inline-block after:absolute after:bg-[var(--primary-color)] after:w-1/2 after:h-full after:right-0 after:top-0 after:rotate-60 after:animate-[wiggle_2s_ease-in-out_infinite] overflow-hidden">
                  <Image
                    src="/images/scan-vehicle-license-disc-for-spares.webp"
                    alt="Scan vehicle license disc for spares"
                    width={500}
                    height={915}
                    className="w-sm h-full m-auto relative z-1 bg-gray-50"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    priority
                    quality={70}
                    placeholder="blur"
                    blurDataURL="/images/scan-vehicle-license-disc-for-spares.webp"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lazy load trigger point (kept for content loading) */}
      <div ref={lazyLoadRef} className="py-15">
        <div className="container">
          <div className="md:w-8/11 xl:w-4/5 m-auto">
            <div className="flex flex-row max-lg:flex-col justify-center">
              <div className="basis-4/12 max-lg:basis-12/12 max-lg:text-center">
                <div className="relative p-1 inline-block after:absolute after:bg-[var(--primary-color)] after:w-1/2 after:h-full after:right-0 after:top-0 after:rotate-60 overflow-hidden after:animate-[wiggle_2s_ease-in-out_infinite] ">
                  <Image src="/images/snap-a-picture-of-your-vehicle-part.webp" alt="Snap a picture of your vehicle part" width={500} height={915} className="w-sm h-full m-auto relative z-1 bg-white" priority quality={85} placeholder="blur" blurDataURL="/images/snap-a-picture-of-your-vehicle-part.webp" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
                </div>
              </div>
              <div className="basis-8/12 max-lg:basis-12/12 mt-10 xl:pt-20">
                <div className="md:pl-10">
                  <h2 className="text-primary text-2xl sm:text-3xl md:text-4xl/10 font-bold uppercase mb-4 border-s-[5px] border-[var(--primary-color)] pl-5">Step 2: Don&apos;t Know the Part Name? Just Snap a Photo.</h2>
                  <p className="mb-3 text-md lg:text-xl">You shouldn&apos;t have to become a mechanic just to find the right part. Take a clear picture, upload it with a short description, and let suppliers respond with what they have available.</p>
                  
                  <p className="mt-4 text-primary max-[640px]:text-lg max-[640px]:text-center">Car Parts Finder App available on Android, iPhone and Huawei App Stores</p>
                  <div className="flex flex-col items-center w-8/12 sm:w-full m-auto sm:flex-row md:gap-5 mt-3">
                    <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank">
                      <Image src={'/images/app-store-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download iPhone" />
                    </Link>
                    <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank">
                      <Image src={'/images/google-play-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Android" />
                    </Link>
                    <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank">
                      <Image src={'/images/app-gallery-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Huawei" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rest of your step sections... */}
      <div className="bg-gray-50 py-15">
        <div className="container">
          <div className="md:w-8/11 xl:w-4/5 m-auto">
            <div className="flex flex-row max-lg:flex-col justify-center">
              <div className="basis-8/12 max-lg:basis-12/12 max-lg:order-2 mt-10 xl:pt-20 xl:pr-10">
                <h2 className="text-primary text-2xl sm:text-3xl md:text-4xl/10 font-bold uppercase mb-4 border-s-[5px] border-[var(--primary-color)] pl-5">Step 3: Find Spare Shops & Scrap Yards Near You — Fast and Local</h2>
                <p className="mb-3 text-md lg:text-xl">Auto-detect your location or enter it manually. We connect you with spare shops and scrap yard suppliers within a 20km radius — so instead of searching one supplier at a time, you get multiple options at once.</p>
                <p className="mt-4 text-primary max-[640px]:text-lg max-[640px]:text-center">Car parts app free download - Car parts app for android</p>
                <div className="flex flex-col items-center w-8/12 sm:w-full m-auto sm:flex-row md:gap-5 mt-3">
                  <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank">
                    <Image src={'/images/app-store-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download iPhone" />
                  </Link>
                  <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank">
                    <Image src={'/images/google-play-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Android" />
                  </Link>
                  <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank">
                    <Image src={'/images/app-gallery-btn.webp'} width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Huawei" />
                  </Link>
                </div>
              </div>
              <div className="basis-4/12 max-lg:basis-12/12 max-lg:order-1 text-right max-lg:text-center">
                <div className="relative p-1 inline-block after:absolute after:bg-[var(--primary-color)] after:w-1/2 after:h-full after:right-0 after:top-0 after:rotate-60 overflow-hidden after:animate-[wiggle_2s_ease-in-out_infinite]">
                  <Image
                    src="/images/request-for-spares-based-on-your-location.webp"
                    alt="Request for spares based on your location"
                    width={500}
                    height={915}
                    className="w-sm h-full m-auto relative z-1 bg-gray-50"
                    quality={70}
                    placeholder="blur"
                    blurDataURL="/images/request-for-spares-based-on-your-location.webp"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-15">
        <div className="container">
          <div className="md:w-8/11 xl:w-4/5 m-auto">
            <div className="flex flex-row max-lg:flex-col justify-center">
              <div className="basis-4/12 max-lg:basis-12/12 max-lg:text-center">
                <div className="relative p-1 inline-block after:absolute after:bg-[var(--primary-color)] after:w-1/2 after:h-full after:right-0 after:top-0 after:rotate-60 overflow-hidden after:animate-[wiggle_2s_ease-in-out_infinite] ">
                  <Image
                      src="/images/select-best-car-spare-quote.webp"
                      alt="Select best car spare quote"
                      width={500}
                      height={915}
                      className="w-sm h-full m-auto relative z-1 bg-white"
                      quality={85}
                      placeholder="blur"
                      blurDataURL="/images/select-best-car-spare-quote.webp"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />
                </div>
              </div>
              <div className="basis-8/12 max-lg:basis-12/12 mt-10 xl:pt-20">
                <div className="md:pl-10">
                  <h2 className="text-primary text-2xl sm:text-3xl md:text-4xl/10 font-bold uppercase mb-4 border-s-[5px] border-[var(--primary-color)] pl-5">Step 4: Compare Quotes. Choose the Best Deal. Get Back on the Road.</h2>
                  <p className="mb-3 text-md lg:text-xl">Receive quotes from multiple trusted spare shops and scrap yards. Compare pricing, availability and suppliers — new, used, OEM or aftermarket — then choose what works for your vehicle and your budget.</p>
                  <p className="mt-4 text-primary max-[640px]:text-lg max-[640px]:text-center">Car parts app south africa</p>
                  <div className="flex flex-col items-center w-8/12 sm:w-full m-auto sm:flex-row md:gap-5 mt-3">
                    <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank">
                      <Image src="/images/app-store-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download iPhone" priority quality={85} placeholder="blur" blurDataURL="/images/app-store-btn.webp" />
                    </Link>
                    <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank">
                      <Image src="/images/google-play-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Android" priority quality={85} placeholder="blur" blurDataURL="/images/google-play-btn.webp" />
                    </Link>
                    <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank">
                      <Image src="/images/app-gallery-btn.webp" width={486} height={142} className="w-full scale-80  md:scale-100" alt="Car Parts App Download Huawei" priority quality={85} placeholder="blur" blurDataURL="/images/app-gallery-btn.webp" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Render popup conditionally: only when user scrolls past "New & Used Car Parts" heading OR when user has triggered it */}
      {typeof window !== 'undefined' && (popupTriggerInView || (userTriggeredPopup && isPopupOpen)) && (
        <RequestItemFormPopup 
          currentPopupType={popupType}
          isContextOpen={isPopupOpen}
          onClose={resetPopup}
          initialData={getInitialData()}
        />
      )}

      {/* Lazy-loaded content after scroll */}
      {lazyLoadInView && (
        <>
          {/* What Are You Really Buying — confidence/assurance section */}
          <section className="py-12 md:py-16 bg-white">
            <div className="container max-w-4xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-3">
                What Are You Really Buying?
              </h2>
              <p className="text-center text-gray-500 mb-10 text-base md:text-lg">
                You&apos;re not just buying a car part. You&apos;re buying{" "}
                <strong className="text-gray-800">confidence that you&apos;re making the right choice.</strong>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {[
                  { q: "Is this the right part for my vehicle?", a: "Scan your license disc so every supplier quote matches your exact make, model and year." },
                  { q: "Am I getting a fair price?", a: "Compare multiple quotes side by side from different suppliers before you decide." },
                  { q: "Can someone actually supply it?", a: "Only suppliers with your part available respond — no more dead ends." },
                  { q: "Can I find a quality used alternative?", a: "Choose from new, OEM, aftermarket or quality used parts — whatever suits your budget." },
                ].map(({ q, a }) => (
                  <div key={q} className="bg-gray-50 rounded-lg p-5 border border-gray-100">
                    <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{q}</p>
                    <p className="text-gray-600 text-sm">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="py-15 bg-red-50">
            <StatsCounter />
          </div>

          {/* New. Used. OEM. Aftermarket. — lightweight trust section */}
          <section className="py-10 md:py-14 bg-white border-y border-gray-100">
            <div className="container max-w-3xl mx-auto px-4 text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                New. Used. OEM. Aftermarket.
              </h2>
              <p className="text-gray-600 text-base md:text-lg mb-6">
                Find the option that makes sense for your vehicle and your budget. You choose — we connect you with suppliers who have it.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {["New Parts", "Quality Used Parts", "OEM Components", "Aftermarket Alternatives", "Scrap Yard Stock"].map((tag) => (
                  <span key={tag} className="inline-block bg-gray-100 text-gray-700 text-sm font-medium px-4 py-2 rounded-full border border-gray-200">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <div className="pt-15">
            {/* POPUP TRIGGER POINT - ref added here to trigger popup when this heading comes into view */}
            <h2 ref={popupTriggerRef} className="text-primary text-2xl sm:text-3xl md:text-4xl font-bold uppercase mb-4 text-center">
                New & Used Car Parts Just Listed
            </h2>             
            <CategoryWithProduct categoryId={ENV.SUB_CATEGORY_ID} />
          </div>

          {/* One Place For Your Automotive Needs — feature grid */}
          <section className="py-12 md:py-16 bg-gray-50">
            <div className="container max-w-5xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">One Place For Your Automotive Needs</h2>
              <p className="text-center text-gray-500 mb-10 text-base md:text-lg">
                From finding the part to choosing a mechanic — more of what you need, in one place.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  { icon: "🔎", title: "Find the Part", desc: "Search the marketplace or submit a request when you can't find what you need." },
                  { icon: "📸", title: "Snap & Request", desc: "Upload a photo and your vehicle details — no need to know the part name." },
                  { icon: "💰", title: "Compare Quotes", desc: "Receive offers from multiple suppliers and compare instead of relying on one price." },
                  { icon: "🏪", title: "Find Suppliers", desc: "Connect with spare shops, scrap yards and automotive service providers near you." },
                  { icon: "🔧", title: "Automotive Services", desc: "Find mechanics, auto electricians, panel beaters and roadside assistance." },
                  { icon: "📱", title: "Take It With You", desc: "Search, receive notifications and manage your requests from your mobile device." },
                ].map(({ icon, title, desc }) => (
                  <div key={title} className="bg-white rounded-lg p-5 border border-gray-100 flex flex-col gap-2">
                    <span className="text-2xl" aria-hidden="true">{icon}</span>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{title}</p>
                    <p className="text-gray-500 text-xs sm:text-sm">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="bg-gray-50 py-10 md:py-15">
            <ProvinceWithCities />
          </div>

          <div className="testimonials bg-red-50 py-10 md:py-15 px-5 md:px-15">
            <Testimonials />
          </div>

          {/* FAQ section — AEO/voice search optimisation (no schema needed here, already in JSON-LD) */}
          <section className="py-12 md:py-16 bg-white border-t border-gray-100">
            <div className="container max-w-3xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">
                Common Questions
              </h2>
              <div className="divide-y divide-gray-100">
                {[
                  { q: "How do I find car parts near me?", a: "Upload a photo of the part, enter your vehicle details and location. Suppliers within 20km respond with what they have — no need to call around." },
                  { q: "Can I find used or second-hand car parts?", a: "Yes. CarSparesEzyFind connects you with scrap yards and suppliers offering new, used, OEM and aftermarket parts so you can compare and choose." },
                  { q: "Do I need to know the part name to request a quote?", a: "No. Take a photo of the part, describe the problem and include your vehicle details. Suppliers will identify it and respond with options." },
                  { q: "Is CarSparesEzyFind free to use for buyers?", a: "Yes. Searching for parts, submitting requests and receiving quotes is free for buyers. You only pay suppliers for parts you choose to purchase." },
                ].map(({ q, a }, i) => (
                  <details key={i} className="group py-4">
                    <summary className="flex justify-between items-center cursor-pointer font-semibold text-gray-900 text-sm sm:text-base list-none">
                      {q}
                      <span className="text-gray-400 group-open:rotate-180 transition-transform ml-4 shrink-0">▾</span>
                    </summary>
                    <p className="mt-3 text-gray-600 text-sm">{a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <div className="container py-10 md:py-15">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-primary text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase mb-6 leading-tight">
                Stop Waiting For Customers To Walk Through Your Door.
              </h2>
              <p className="text-gray-700 text-lg sm:text-xl md:text-2xl font-semibold mb-4 leading-relaxed">
                Own a Spare Shop, Scrap Yard or Automotive Business?
              </p>
              <p className="text-gray-600 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
                Your inventory is valuable. Your expertise is valuable. List your parts, products and services on CarSparesEzyFind and put your business in front of people actively searching for automotive solutions.
              </p>
              <h3 className="text-primary text-base sm:text-lg md:text-xl lg:text-2xl font-bold uppercase mb-10 tracking-wide">
                Select Subscription Plan Below
              </h3>
            </div>
          </div>
          <div className="container">
            <SubscriptionPlan />
          </div>
          
          <DownloadApp />

          <div className="py-10 md:py-15">
            <PartnerLogosSlider />
          </div>
        </>
      )}
    </>
  )
}